import { getFlexiblePrecision, getFlexibleRange, getTaskEnd, getTaskStartDate, isHourlySchedule } from './scheduleUtils';
import { getLocalDateKey } from './dateUtils';
import { PlannerTaskInfo, TimelineScale } from './types';

export const calendarParents: Partial<Record<TimelineScale, TimelineScale>> = {
    day: 'month', month: 'year', year: 'years',
};

export const getMonthCells = (monthDate: Date): Array<Date | null> => {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const dayCount = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
    const cells: Array<Date | null> = Array.from({ length: offset }, () => null);
    for (let day = 1; day <= dayCount; day += 1) cells.push(new Date(first.getFullYear(), first.getMonth(), day));
    while (cells.length % 7) cells.push(null);
    return cells;
};

const getCalendarRange = (task: PlannerTaskInfo['task']) => {
    const range = getFlexibleRange(task);
    const precision = getFlexiblePrecision(task);
    if (precision === 'month') {
        const end = new Date(range.end.getFullYear(), range.end.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start: range.start, end };
    }
    if (precision) return range;
    if (isHourlySchedule(task)) return { start: range.start, end: getTaskEnd(task) };
    if (!task.duration) return range;
    return { start: range.start, end: new Date(range.start.getTime() + task.duration * 60_000) };
};

export const getTasksForDate = (tasks: PlannerTaskInfo[], date: Date) => tasks.filter(({ task }) => {
    const precision = getFlexiblePrecision(task);
    if (precision === 'month' || precision === 'year' || !getTaskStartDate(task)) return false;
    const { start, end } = getCalendarRange(task);
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayEnd = new Date(dayStart); dayEnd.setHours(23, 59, 59, 999);
    return start <= dayEnd && (precision ? end >= dayStart : end > dayStart);
});

export const getTasksForMonth = (tasks: PlannerTaskInfo[], monthDate: Date) => {
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return tasks.filter(({ task }) => getTaskStartDate(task) && (() => {
        const range = getCalendarRange(task);
        return range.start <= end && range.end >= start;
    })());
};

const getCalendarPriority = ({ task }: PlannerTaskInfo) => {
    const precision = getFlexiblePrecision(task);
    if (!precision) return 1;
    if (precision !== 'day') return 2;
    const { start, end } = getFlexibleRange(task);
    return start.toDateString() === end.toDateString() ? 2 : 0;
};

export const orderCalendarTasks = (tasks: PlannerTaskInfo[]) => [...tasks].sort((left, right) => {
    if (left.task.completed !== right.task.completed) return left.task.completed ? 1 : -1;
    const priority = getCalendarPriority(left) - getCalendarPriority(right);
    if (priority) return priority;
    return getTaskStartDate(left.task)!.getTime() - getTaskStartDate(right.task)!.getTime();
});

export const getCalendarTaskTimeLabel = (task: PlannerTaskInfo['task'], date?: Date) => {
    if (getFlexiblePrecision(task)) return '';
    const start = getTaskStartDate(task);
    if (!start) return '';
    const end = getTaskEnd(task);
    const format = (value: Date) => `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`;
    if (!date || start.toDateString() === end.toDateString() || date.toDateString() === start.toDateString()) return format(start);
    return date.toDateString() === end.toDateString() ? `până la ${format(end)}` : '';
};

export const getTasksForYear = (tasks: PlannerTaskInfo[], year: number) => (
    tasks.filter(({ task }) => getTaskStartDate(task) && (() => {
        const range = getCalendarRange(task);
        return range.start <= new Date(year, 11, 31, 23, 59, 59, 999) && range.end >= new Date(year, 0, 1);
    })())
);

export const getCalendarTaskSegment = (
    task: PlannerTaskInfo['task'], date: Date, _monthDate: Date
) => {
    const range = getCalendarRange(task);
    const spansMultipleDays = range.start.toDateString() !== range.end.toDateString();
    if (getFlexiblePrecision(task) !== 'day' && !spansMultipleDays) {
        return { className: '', showLabel: true };
    }
    const { start, end } = range;
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    const inclusiveEnd = new Date(end);
    inclusiveEnd.setHours(23, 59, 59, 999);
    const hasBefore = start < dayStart;
    const hasAfter = inclusiveEnd > dayEnd;
    const startsVisibleMonth = hasBefore && date.getDate() === 1;

    if (!hasBefore && hasAfter) return { className: 'is-range range-start', showLabel: true };
    if (!hasBefore && !hasAfter) return { className: 'is-range range-single', showLabel: true };
    if (!hasAfter) {
        return {
            className: 'is-range range-end',
            showLabel: date.getDay() === 1 || startsVisibleMonth,
        };
    }
    if (date.getDay() === 1) return { className: 'is-range range-row-start', showLabel: true };
    if (date.getDay() === 0) {
        return { className: 'is-range range-row-end', showLabel: startsVisibleMonth };
    }
    if (startsVisibleMonth) return { className: 'is-range range-middle', showLabel: true };
    return { className: 'is-range range-middle', showLabel: false };
};

export const getCalendarTaskLanes = (tasks: PlannerTaskInfo[], monthDate: Date) => {
    const lanes = new Map<string, number>();
    let previousDayLanes = new Map<string, number>();

    getMonthCells(monthDate).forEach((date, index) => {
        if (index % 7 === 0) previousDayLanes = new Map();
        if (!date) return;

        const dayTasks = orderCalendarTasks(getTasksForDate(tasks, date));
        const dayLanes = new Map<string, number>();
        const usedLanes = new Set<number>();
        dayTasks.forEach((taskInfo) => {
            const previousLane = previousDayLanes.get(taskInfo.task.id);
            if (previousLane === undefined) return;
            dayLanes.set(taskInfo.task.id, previousLane);
            usedLanes.add(previousLane);
        });
        dayTasks.forEach((taskInfo) => {
            if (dayLanes.has(taskInfo.task.id)) return;
            let lane = 0;
            while (usedLanes.has(lane)) lane += 1;
            dayLanes.set(taskInfo.task.id, lane);
            usedLanes.add(lane);
        });
        dayLanes.forEach((lane, taskId) => lanes.set(`${getLocalDateKey(date)}-${taskId}`, lane));
        previousDayLanes = dayLanes;
    });

    return lanes;
};

export const getCalendarTaskLabelSpan = (
    task: PlannerTaskInfo['task'], date: Date, monthDate: Date
) => {
    const { start, end } = getCalendarRange(task);
    if (start.toDateString() === end.toDateString()) return 1;
    const lastMonthDay = new Date(
        monthDate.getFullYear(), monthDate.getMonth() + 1, 0
    ).getDate();
    const endDay = end.getFullYear() === date.getFullYear() && end.getMonth() === date.getMonth()
        ? end.getDate()
        : lastMonthDay;
    const weekday = (date.getDay() + 6) % 7;
    return Math.max(1, Math.min(7 - weekday, endDay - date.getDate() + 1));
};

export const moveCalendarAnchor = (date: Date, scale: TimelineScale, direction: number) => {
    if (scale === 'day') return new Date(date.getFullYear(), date.getMonth() + direction, 1);
    if (scale === 'month') return new Date(date.getFullYear() + direction, 0, 1);
    if (scale === 'year') return new Date(date.getFullYear() + direction * 12, 0, 1);
    return new Date(date.getFullYear() + direction * 144, 0, 1);
};
